import { selectors } from "../../add-element/cypressSelectors";
import {
  ContentText,
  ContentWrapper,
  HeadlineSmall,
} from "../../content/components";
import { useIsPreviewOrDemo } from "../../contexts/IsPreviewOrDemoContext";
import { useCurrentSite } from "../../contexts/models/SiteContext";
import WeddingRSVPForm from "../../guest-management/forms/RSVPForm/Wedding/WeddingRSVPForm";
import RSVPSearchModal from "../../guest-management/forms/RSVPSearchModal/RSVPSearchModal";
import PageComponent from "../../pages/website/Page";
import { Configuration, Page, RSVPData } from "../../resources/modelTypes";
import { css } from "@emotion/react";
import { Button as FancyButton } from "@minted/fancyclothes";
import { TextInput, theme } from "@minted/minted-components";
import React, { useCallback, useState } from "react";

const rsvpStyles = {
  cookieError: css`
    ${theme.typeStyles.bodyMediumStyles};
    text-align: center;
    color: ${theme.colors.negative800};
    margin-bottom: ${theme.spacing.x2};
  `,
  nameInput: css`
    margin-bottom: ${theme.spacing.x4};
    text-align: left;
  `,
  onlineInvitationWrapper: css`
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;
    padding-right: 20px;
    padding-left: 20px;
  `,
  weddingWebsiteWrapper: (textAlignment) => css`
    max-width: 500px;
    ${theme.media.greaterThan(theme.breakpoints.small)(`
      margin:
        20px
        ${textAlignment === "left" || textAlignment === "center" ? "auto" : "20px"}
        20px
        ${textAlignment === "right" || textAlignment === "center" ? "auto" : "20px"};
    `)}
  `,
};

export type RSVPProps = {
  configuration: Configuration;
  id: string;
  pageData: Page;
  title: string;
};

const RSVP = ({ configuration, id, pageData, title }: RSVPProps) => {
  const site = useCurrentSite();
  const { isDemo, isPreview } = useIsPreviewOrDemo();
  const [rsvpData, setRsvpData] = useState<RSVPData | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showRSVPModal, setShowRSVPModal] = useState(false);
  const isInBrowser = typeof window !== "undefined";
  const hasCookies = isInBrowser && window.navigator.cookieEnabled;

  const handleFindRSVP = useCallback(() => {
    setShowRSVPModal(true);
  }, []);

  const onCancelRsvp = useCallback(() => {
    setRsvpData(null);
    setSearchTerm("");
    setShowRSVPModal(false);
  }, []);

  return (
    // This has to exist way up here to avoid dealing with inherited styling
    rsvpData ? (
      <WeddingRSVPForm
        contact={rsvpData.contact}
        events={rsvpData.events}
        guests={rsvpData.guests}
        hasExistingRsvps={rsvpData.hasExistingRsvps}
        onCancelRsvp={onCancelRsvp}
      />
    ) : (
      <PageComponent
        id={id}
        pageData={pageData}
        removeSpacing
        title={rsvpData ? "" : title}
      >
        <ContentWrapper
          extraCss={
            site.isOnlineInvitation
              ? []
              : [rsvpStyles.weddingWebsiteWrapper(configuration.textAlignment)]
          }
          imageAlignment="top"
          noVerticalMargin
          textAlignment={configuration.textAlignment}
        >
          {isInBrowser && (
            <>
              <ContentText textAlignment={configuration.textAlignment}>
                <HeadlineSmall>
                  {configuration?.rsvpSubheading || "Enter Your Name to RSVP"}
                </HeadlineSmall>
              </ContentText>
              {hasCookies ? (
                <>
                  {showRSVPModal && (
                    <RSVPSearchModal
                      closeModal={() => setShowRSVPModal(false)}
                      configuration={configuration}
                      initialSearchTerm={searchTerm}
                      openInvitation={false}
                      setRsvpData={setRsvpData}
                      site={site}
                    />
                  )}
                  <div
                    css={rsvpStyles.nameInput}
                    data-cy={selectors.rsvp_searchNameInput}
                  >
                    <TextInput
                      disabled={isPreview || isDemo}
                      onChange={(event) => {
                        setSearchTerm(event.target.value);
                      }}
                      onKeyDown={(event) =>
                        event.key === "Enter" && setShowRSVPModal(true)
                      }
                      size="small"
                      touched
                      value={searchTerm}
                    />
                  </div>
                  <div data-cy={selectors.rsvp_findRsvpButton}>
                    <FancyButton
                      block={true}
                      disabled={isPreview || isDemo}
                      onClick={handleFindRSVP}
                      text="Find RSVP"
                    />
                  </div>
                </>
              ) : (
                <div
                  css={rsvpStyles.cookieError}
                  data-cy={selectors.rsvp_cookieError}
                >
                  Cookies are required to submit your RSVP. Please enable
                  cookies in your browser.
                </div>
              )}
            </>
          )}
        </ContentWrapper>
      </PageComponent>
    )
  );
};

export default RSVP;
